import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env';

import {
  AudienceFilterParams,
  AudienceSystemsResponse,
  CampaignPreview,
  AudiencePreview,
  CreateMailingCampaignPayload,
  CreateMailingTemplatePayload,
  FolioResolution,
  FolioSourcePayload,
  GetAudienceSystemsParams,
  GetMailingCampaignsParams,
  GetMailingRecipientsParams,
  MailingAsset,
  MailingCampaign,
  MailingCampaignsResponse,
  MailingFolioList,
  MailingFolioListDetail,
  MailingFolioListsResponse,
  MailingPreviewPayload,
  MailingRecipientsResponse,
  MailingTemplate,
  MailingTemplatesResponse,
  RenderedEmail,
  ReserveMailingAssetPayload,
  ReservedMailingAsset,
  SaveFolioListPayload,
  SavedFolioList,
  SendTestResult,
  UpdateMailingCampaignPayload,
  UpdateMailingTemplatePayload,
} from '../../../interfaces/mailing.interface';
import { MailingApiService } from './mailing-api.service';

/**
 * Cliente de la API del mailing a clientes (`/mailing`).
 *
 * Sigue el contrato de `backend/src/modules/mailing/README.md`: respuestas en
 * `camelCase`, listados `{ <plural>, pagination }`, listas de estatus unidas
 * por coma en el query string y los filtros de audiencia tal cual viajan a
 * `GET /systems`.
 */
@Injectable()
export class MailingHttpService extends MailingApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiGateway}mailing`;
  private readonly templatesUrl = `${this.baseUrl}/templates`;
  private readonly campaignsUrl = `${this.baseUrl}/campaigns`;
  private readonly assetsUrl = `${this.baseUrl}/assets`;
  private readonly listsUrl = `${this.baseUrl}/lists`;

  // ── Plantillas ───────────────────────────────────────────────────────

  getTemplates(includeInactive = false): Observable<MailingTemplatesResponse> {
    let params = new HttpParams();
    if (includeInactive) {
      params = params.set('include_inactive', 'true');
    }
    return this.http.get<MailingTemplatesResponse>(this.templatesUrl, { params });
  }

  createTemplate(payload: CreateMailingTemplatePayload): Observable<MailingTemplate> {
    return this.http.post<MailingTemplate>(this.templatesUrl, payload);
  }

  updateTemplate(
    templateId: number,
    payload: UpdateMailingTemplatePayload,
  ): Observable<MailingTemplate> {
    return this.http.put<MailingTemplate>(`${this.templatesUrl}/${templateId}`, payload);
  }

  deleteTemplate(templateId: number): Observable<MailingTemplate> {
    return this.http.delete<MailingTemplate>(`${this.templatesUrl}/${templateId}`);
  }

  // ── Campañas ─────────────────────────────────────────────────────────

  getCampaigns(params: GetMailingCampaignsParams): Observable<MailingCampaignsResponse> {
    return this.http.get<MailingCampaignsResponse>(this.campaignsUrl, {
      params: this.buildCampaignsParams(params),
    });
  }

  getCampaign(campaignId: number): Observable<MailingCampaign> {
    return this.http.get<MailingCampaign>(`${this.campaignsUrl}/${campaignId}`);
  }

  createCampaign(payload: CreateMailingCampaignPayload): Observable<MailingCampaign> {
    return this.http.post<MailingCampaign>(this.campaignsUrl, payload);
  }

  updateCampaign(
    campaignId: number,
    payload: UpdateMailingCampaignPayload,
  ): Observable<MailingCampaign> {
    return this.http.put<MailingCampaign>(`${this.campaignsUrl}/${campaignId}`, payload);
  }

  deleteCampaign(campaignId: number): Observable<MailingCampaign> {
    return this.http.delete<MailingCampaign>(`${this.campaignsUrl}/${campaignId}`);
  }

  getRecipients(
    campaignId: number,
    params: GetMailingRecipientsParams,
  ): Observable<MailingRecipientsResponse> {
    return this.http.get<MailingRecipientsResponse>(
      `${this.campaignsUrl}/${campaignId}/recipients`,
      { params: this.buildRecipientsParams(params) },
    );
  }

  /**
   * El backend responde 202: la campaña ya quedó en `queued` y el fan-out se
   * disparó en segundo plano. Lo que llega es la campaña, no un acuse vacío.
   */
  sendCampaign(campaignId: number): Observable<MailingCampaign> {
    return this.http.post<MailingCampaign>(`${this.campaignsUrl}/${campaignId}/send`, {});
  }

  // ── Audiencia, vista previa y prueba ─────────────────────────────────

  previewAudience(filters: AudienceFilterParams, sampleSize?: number): Observable<AudiencePreview> {
    return this.http.get<AudiencePreview>(`${this.baseUrl}/audience`, {
      params: this.buildAudienceParams(filters, sampleSize),
    });
  }

  getAudienceSystems(params: GetAudienceSystemsParams): Observable<AudienceSystemsResponse> {
    return this.http.get<AudienceSystemsResponse>(`${this.baseUrl}/audience/systems`, {
      params: this.buildAudienceSystemsParams(params),
    });
  }

  previewCampaign(campaignId: number, recipientId?: number): Observable<CampaignPreview> {
    let params = new HttpParams();
    if (recipientId !== undefined) {
      params = params.set('recipient_id', String(recipientId));
    }

    return this.http.get<CampaignPreview>(`${this.campaignsUrl}/${campaignId}/preview`, { params });
  }

  previewEmail(payload: MailingPreviewPayload): Observable<RenderedEmail> {
    return this.http.post<RenderedEmail>(`${this.baseUrl}/preview`, payload);
  }

  sendTestEmail(payload: MailingPreviewPayload): Observable<SendTestResult> {
    return this.http.post<SendTestResult>(`${this.baseUrl}/test-send`, payload);
  }

  // ── Listas de folios ─────────────────────────────────────────────────

  getFolioLists(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Observable<MailingFolioListsResponse> {
    let httpParams = this.buildPageParams(params);
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    return this.http.get<MailingFolioListsResponse>(this.listsUrl, { params: httpParams });
  }

  getFolioList(
    listId: number,
    params: { page?: number; limit?: number },
  ): Observable<MailingFolioListDetail> {
    return this.http.get<MailingFolioListDetail>(`${this.listsUrl}/${listId}`, {
      params: this.buildPageParams(params),
    });
  }

  /**
   * El archivo viaja en base64 dentro del body: una lista de 10,000 folios
   * pesa ~130 KB, muy por debajo del límite de API Gateway, y así se evita
   * multipart y un viaje a S3 para algo que se descarta al parsearse.
   */
  previewFolioList(payload: FolioSourcePayload): Observable<FolioResolution> {
    return this.http.post<FolioResolution>(`${this.listsUrl}/preview`, payload);
  }

  createFolioList(payload: SaveFolioListPayload): Observable<SavedFolioList> {
    return this.http.post<SavedFolioList>(this.listsUrl, payload);
  }

  updateFolioList(
    listId: number,
    payload: Partial<SaveFolioListPayload>,
  ): Observable<SavedFolioList> {
    return this.http.put<SavedFolioList>(`${this.listsUrl}/${listId}`, payload);
  }

  deleteFolioList(listId: number): Observable<MailingFolioList> {
    return this.http.delete<MailingFolioList>(`${this.listsUrl}/${listId}`);
  }

  previewListAudience(listId: number, sampleSize?: number): Observable<AudiencePreview> {
    let httpParams = new HttpParams();
    if (sampleSize !== undefined) {
      httpParams = httpParams.set('sample_size', String(sampleSize));
    }
    return this.http.get<AudiencePreview>(`${this.listsUrl}/${listId}/audience`, {
      params: httpParams,
    });
  }

  // ── Imágenes ─────────────────────────────────────────────────────────

  reserveAsset(payload: ReserveMailingAssetPayload): Observable<ReservedMailingAsset> {
    return this.http.post<ReservedMailingAsset>(this.assetsUrl, payload);
  }

  /**
   * El PUT va directo a S3, no a la API. El `Content-Type` tiene que ser idéntico
   * al declarado al reservar porque la firma lo incluye; mandar otro —o dejar que
   * el navegador lo adivine— hace que S3 responda 403.
   */
  uploadToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  }

  confirmAsset(assetId: number): Observable<MailingAsset> {
    return this.http.post<MailingAsset>(`${this.assetsUrl}/${assetId}/confirm`, {});
  }

  // ── Query params ─────────────────────────────────────────────────────

  /**
   * `status=draft,sent`, `search`, `page` y `limit`. Los filtros vacíos no
   * viajan: el backend tiene sus defaults y la URL refleja sólo lo elegido.
   * @param params Filtros y paginación de la pantalla
   */
  private buildCampaignsParams(params: GetMailingCampaignsParams): HttpParams {
    let httpParams = new HttpParams();

    if (params.status?.length) {
      httpParams = httpParams.set('status', params.status.join(','));
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', String(params.limit));
    }

    return httpParams;
  }

  /**
   * `status=failed,skipped`, `page` y `limit` de los destinatarios.
   * @param params Filtros y paginación de la pantalla
   */
  private buildRecipientsParams(params: GetMailingRecipientsParams): HttpParams {
    let httpParams = new HttpParams();

    if (params.status?.length) {
      httpParams = httpParams.set('status', params.status.join(','));
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', String(params.limit));
    }

    return httpParams;
  }

  /**
   * `page` y `limit` a secas, que es toda la paginación de las listas de
   * folios y de sus renglones.
   * @param params Paginación de la pantalla
   */
  private buildPageParams(params: { page?: number; limit?: number }): HttpParams {
    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', String(params.limit));
    }

    return httpParams;
  }

  /**
   * Los filtros de audiencia ya son query params de `GET /systems`, así que
   * viajan tal cual; sólo se descartan los vacíos y se suma `sample_size`.
   * @param filters Filtros de la campaña
   * @param sampleSize Tamaño de la muestra (el backend usa 10 si no va)
   */
  private buildAudienceParams(filters: AudienceFilterParams, sampleSize?: number): HttpParams {
    let httpParams = new HttpParams();

    for (const [key, value] of Object.entries(filters ?? {})) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      httpParams = httpParams.set(key, value.trim());
    }
    if (sampleSize !== undefined) {
      httpParams = httpParams.set('sample_size', String(sampleSize));
    }

    return httpParams;
  }

  /**
   * Query de la tabla de alcance: `list_id` **o** los filtros sueltos, más
   * `page` y `limit`.
   *
   * Con `listId` los filtros ni se miran. Mandar los dos es 422
   * `INVALID_PAYLOAD_LIST_ID`, y una campaña por lista guarda sus filtros en
   * `{}` pero la pantalla puede traer los suyos cargados: ramificar aquí evita
   * que ese resto arrastrado tire la petición.
   *
   * @param params Origen de la audiencia y paginación
   */
  private buildAudienceSystemsParams(params: GetAudienceSystemsParams): HttpParams {
    let httpParams = this.buildPageParams(params);

    if (params.listId !== undefined) {
      return httpParams.set('list_id', String(params.listId));
    }

    for (const [key, value] of Object.entries(params.filters ?? {})) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      httpParams = httpParams.set(key, value.trim());
    }

    return httpParams;
  }
}
